import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { clearSessionAndRedirect } from "../helpers/authHelpers";

import { TokenPayload } from "@/types/response/LoginResponses";

// Runtime configuration via window global (set by config.js at deployment)
declare global {
  interface Window {
    __APP_CONFIG__?: {
      API_URL?: string;
    };
  }
}

export const baseURL =
  window.__APP_CONFIG__?.API_URL ||
  "https://localhost:44390";

export const instance = axios.create({
  baseURL,
  paramsSerializer: {
    indexes: null,
  },
});

const excludedPaths = ["/login", "/register"];

const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

export function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (!decoded.exp) return false;
    return decoded.exp < Date.now() / 1000 + TOKEN_EXPIRY_BUFFER_SECONDS;
  } catch {
    return true;
  }
}

function storeTokenData(accessToken: string, refreshToken: string) {
  localStorage.setItem("accessToken", accessToken);
  window.dispatchEvent(
    new StorageEvent("local-storage", { key: "accessToken" }),
  );

  localStorage.setItem("refreshToken", refreshToken);

  const decodedToken: TokenPayload = jwtDecode(accessToken);

  localStorage.setItem("firstname", decodedToken.firstName || "");
  localStorage.setItem("lastname", decodedToken.lastName || "");
  localStorage.setItem("email", decodedToken.email || "");

  if (decodedToken.infix) {
    localStorage.setItem("infix", decodedToken.infix);
  } else {
    localStorage.removeItem("infix");
  }
}

type RefreshSubscriber = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

export async function doRefresh(): Promise<string> {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshSubscribers.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  const tokenBeforeRefresh = localStorage.getItem("accessToken");

  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    isRefreshing = false;
    clearSessionAndRedirect();
    return Promise.reject(new Error("No refresh token"));
  }

  try {
    const { data } = await axios.post(
      `${baseURL}/api/v2/Auth/refresh`,
      { refreshToken },
    );
    storeTokenData(data.accessToken, data.refreshToken);

    refreshSubscribers.forEach((sub) => sub.resolve(data.accessToken));
    refreshSubscribers = [];
    return data.accessToken;
  } catch (error) {
    // Cross-tab fallback: another tab may have refreshed while our attempt failed
    const freshToken = localStorage.getItem("accessToken");
    if (freshToken && freshToken !== tokenBeforeRefresh) {
      refreshSubscribers.forEach((sub) => sub.resolve(freshToken));
      refreshSubscribers = [];
      return freshToken;
    }

    refreshSubscribers.forEach((sub) => sub.reject(error));
    refreshSubscribers = [];
    clearSessionAndRedirect();
    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
}

instance.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem("accessToken");

    if (
      accessToken &&
      !excludedPaths.some((path) => config.url?.includes(path)) &&
      isTokenExpired(accessToken)
    ) {
      const newToken = await doRefresh();
      config.headers.Authorization = `Bearer ${newToken}`;
      return config;
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (excludedPaths.some((path) => originalRequest.url?.includes(path))) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Fast path: another tab may have already refreshed the token.
      const sentToken =
        originalRequest.headers.Authorization?.replace("Bearer ", "") || "";
      const currentToken = localStorage.getItem("accessToken");
      if (sentToken && currentToken && currentToken !== sentToken) {
        originalRequest.headers.Authorization = `Bearer ${currentToken}`;
        return instance(originalRequest);
      }

      try {
        const newToken = await doRefresh();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return await instance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);
